import { Button, Link, Stack } from '@mui/material';
import { ArrowCounterClockwiseIcon, PlusIcon } from '@phosphor-icons/react';
import {
  FlowCaseSensitivity,
  FlowFieldDataType,
  type FlowFilterConfig,
  FlowFilterMatchRule,
  FlowFilterOperation,
} from 'flow-schema';
import * as uuid from 'uuid';

import { Text, isDefined, isTruthy } from 'common';

import { FilterConditionCard } from './FilterConditionCard.tsx';
import { useSelectedNodeField } from './useSelectedNodeField.ts';

export const FILTER_MATCH_RULE_NAMES: Record<FlowFilterMatchRule, string> = {
  [FlowFilterMatchRule.All]: 'all',
  [FlowFilterMatchRule.Any]: 'any',
};

export function FilterConditionFields(props: {
  supportedDataTypes: FlowFieldDataType[];
  fieldsToFilter: string[];
}) {
  const { supportedDataTypes, fieldsToFilter } = props;
  const { value: filterMatchRule, setValue: setFilterMatchRule } =
    useSelectedNodeField<FlowFilterMatchRule>('config.filterConfig.filterMatchRule');

  const { value: filterConfig, setValue: setFilterConfig } = useSelectedNodeField<
    FlowFilterConfig | undefined
  >('config.filterConfig');

  return (
    <>
      <Stack gap={1}>
        <Stack direction="row" gap={0.5} alignItems="center">
          <Text variant="heading5" bold>
            Conditions match
          </Text>
          {isTruthy(filterConfig?.filterConditions.length) && (
            <Link
              component="button"
              onClick={() => {
                setFilterMatchRule(
                  filterMatchRule === FlowFilterMatchRule.All
                    ? FlowFilterMatchRule.Any
                    : FlowFilterMatchRule.All
                );
              }}
            >
              {FILTER_MATCH_RULE_NAMES[filterMatchRule ?? FlowFilterMatchRule.All]}{' '}
              <ArrowCounterClockwiseIcon size={12} />
            </Link>
          )}
        </Stack>
        {filterConfig?.filterConditions.map((condition, index) => (
          <FilterConditionCard
            key={condition.id}
            filterConditionsFieldNamePrefix={`config.filterConfig.filterConditions.${index}`}
            supportedDataTypes={supportedDataTypes}
            fieldsToFilter={fieldsToFilter}
            onDelete={() => {
              if (!isDefined(filterConfig)) {
                return;
              }
              setFilterConfig({
                ...filterConfig,
                filterConditions: filterConfig.filterConditions.filter(
                  (filterCondition) => filterCondition.id !== condition.id
                ),
              });
            }}
          />
        ))}
        <Button
          variant="outlined"
          sx={{ alignSelf: 'flex-start', gap: 1 }}
          aria-label="Add filter condition"
          onClick={() => {
            const filterMatchRule = filterConfig?.filterMatchRule || FlowFilterMatchRule.All;
            setFilterConfig({
              ...(filterConfig || {}),
              filterMatchRule,
              filterConditions: [
                ...(filterConfig?.filterConditions || []),
                {
                  id: uuid.v4(),
                  fieldToFilter: '',
                  value: '',
                  dataType: FlowFieldDataType.String,
                  operation: FlowFilterOperation.Equals,
                  caseSensitivity: FlowCaseSensitivity.ExactMatch,
                },
              ],
            });
          }}
        >
          <PlusIcon size={12} />
          Add condition
        </Button>
      </Stack>
    </>
  );
}
